import WalletRequiredWrapper from 'components/WalletRequiredWrapper'
import { useMarketplace, useMarketplaceInterface } from 'context/Marketplace'
import { AssetType, AssetWithBalance } from 'core/logic/asset/asset.types'
import ROUTES, {
  TO_COLLECTION_ITEM_DETAIL,
  TO_MARKETPLACE_OFFER_DETAILS,
} from 'core/modules/router'
import {
  useState,
  useMemo,
  useEffect,
  Fragment,
  PropsWithChildren,
} from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { SettleOperationType } from '../SettleOperation/SettleOperation.types'
import SettleOperationLogic from '../SettleOperation/SettleOperation.logic'
import {
  CreateOfferFormMode,
  OfferForm,
  PREDEFINED_DEADLINES,
} from './CreateOffer.types'
import CreateOfferView from './CreateOffer.view'
import Transitions from 'components/Transitions'
import { useUser } from 'core/logic/user'
import { DeepPartial } from 'react-hook-form'
import { useRemainingAvailableTokens } from 'core/logic/token/token.hook'
import Confirm from './Confirm'
import useCustomHistory from '@onepercentio/one-ui/hooks/useCustomHistory'
import {
  useMyCollectionItems,
  useUpToDateMyCollectionItems,
} from 'context/MyCollection'
import { useGallery } from 'context/Gallery'
import { ContractType } from 'core/logic/contract/types/types'
import DigitalTwinLogic from './OfferType/DigitalTwin/DigitalTwin.logic'
import ZeroXMarketplace, {
  CustodyZeroXMarketplace,
} from 'service/IMarketplace/ZeroXMarketplace'
import OnchainMarketplace from 'service/IMarketplace/OnchainMarketplace'
import OverlayKYCWrapperLogic from 'openspace/components/OverlayKYCWrapper'
import useQuery from '@onepercentio/one-ui/hooks/utility/useQuery'
import { useScrollToTop } from 'containers/MainRouter/MainRouter.hook'

const INITIAL_FORM: OfferForm = {
  selectedDeadline: PREDEFINED_DEADLINES.A_MONTH,
  selectedQuantity: 1,
  amount: {},
}

const FragmentWrapper = ({ children }: PropsWithChildren) => (
  <Fragment>{children}</Fragment>
)

const isAmountEdited = (original: OfferForm, newInfo: OfferForm) => {
  const currency = Object.keys(original.amount)[0]
  return original.amount[currency]?.amount !== newInfo.amount[currency]?.amount
}

const isQuantityEdited = (original: OfferForm, newInfo: OfferForm) => {
  return original.selectedQuantity !== newInfo.selectedQuantity
}

function CreateOfferLogic({
  onConfirm,
  onEdit,
  compact,
  asset: ownedAsset,
}: {
  onConfirm: (offer: OfferForm, offerId?: string) => void // When we are creating a new offer
  onEdit: (offer: DeepPartial<OfferForm>, offerId: string) => void // When we are editing an existing offer
  compact: boolean
  asset?: AssetWithBalance
}) {
  const { assetId: id, offerId } = useParams<{
    assetId: AssetType['id']
    offerId?: string
  }>()

  const gallery = useGallery(ownedAsset?.galleryId!)
  const { currencies, tax, sales } = useMarketplace()
  const [offerForm, setDetails] = useState<OfferForm>(INITIAL_FORM)
  const { availableForSale } = useRemainingAvailableTokens(ownedAsset, offerId)
  const history = useCustomHistory()

  useEffect(() => {
    if (offerId) sales.getAll()
  }, [id, offerId])

  useEffect(() => {
    if (ownedAsset) {
      setDetails((prev) => ({
        ...prev,
        tokenId: ownedAsset.tokenId,
        cid: ownedAsset.ipfs?.jsonHash,
      }))
    }
  }, [ownedAsset, ownedAsset?.id])

  useEffect(() => {
    if (currencies.length === 1)
      setDetails((prev) => ({
        ...prev,
        amount: {
          [currencies[0]]: {
            checked: true,
            amount: undefined as any,
          },
        },
      }))
  }, [currencies])

  const origOffer = useMemo<OfferForm | undefined>(() => {
    return offerId && ownedAsset
      ? sales.items
          ?.filter((a) => a.assetId === ownedAsset.id)
          ?.reduce(
            (r, i, index, arr) => {
              if (i.offerId === offerId) {
                if (!r.amount[i.currency]) r.amount[i.currency] = {} as any
                r.amount[i.currency]!.amount = i.price
                r.amount[i.currency]!.checked = true
                r.selectedQuantity++
              }

              if (index === arr.length - 1) {
                r.selectedQuantity =
                  r.selectedQuantity / Object.keys(r.amount).length
              }
              return r
            },
            { ...INITIAL_FORM, selectedQuantity: 0 }
          )
      : undefined
  }, [ownedAsset, offerId, sales.items])

  const canContinue = useMemo(() => {
    const hasAnActiveCurrency = currencies.some(
      (curr) => offerForm.amount[curr]?.checked
    )
    const someCurrencyIsNegative = currencies.some(
      (curr) =>
        offerForm.amount[curr]?.checked && offerForm.amount[curr]!.amount < 0
    )
    const someCheckedMissingAmount = !!currencies.find(
      (curr) =>
        offerForm.amount[curr]?.checked && !offerForm.amount[curr]?.amount
    )
    const hasNecessaryInfo =
      hasAnActiveCurrency &&
      !someCheckedMissingAmount &&
      !someCurrencyIsNegative

    // Is an edition and has old information
    if (offerId && origOffer) {
      let isEditingSomeInfo =
        isAmountEdited(origOffer, offerForm) || // Has changed amount
        isQuantityEdited(origOffer, offerForm) // Or has changed amount
      return hasNecessaryInfo && isEditingSomeInfo
    }

    return hasNecessaryInfo
  }, [offerForm, origOffer, currencies, offerId])

  const onConfirmSellOrUpdate = () => {
    // Is edition?
    if (offerId) {
      const onlyEditedFields: DeepPartial<OfferForm> = {
        ...(isAmountEdited(origOffer!, offerForm)
          ? { amount: offerForm.amount }
          : {}),
        ...(isQuantityEdited(origOffer!, offerForm)
          ? { selectedQuantity: offerForm.selectedQuantity }
          : {}),
      }
      onEdit(onlyEditedFields, offerId)
    } else {
      onConfirm(offerForm)
    }
  }

  useEffect(() => {
    if (origOffer) setDetails(origOffer)
  }, [origOffer])

  const { ownedAssetDetails } = useMemo(() => {
    if (!offerId) {
      return {
        ownedAssetDetails: ownedAsset,
      }
    } else {
      return {
        ownedAssetDetails: origOffer ? ownedAsset : undefined,
      }
    }
  }, [origOffer, ownedAsset, offerId])

  useEffect(() => {
    if (availableForSale !== undefined && availableForSale <= 0) {
      history.replace(
        TO_COLLECTION_ITEM_DETAIL(ownedAsset!.id, ownedAsset!.tokenId)
      )
    }
  }, [availableForSale, history, ownedAsset])

  const content = (() => {
    switch (gallery?.smartContract?.type) {
      case ContractType.DigitalTwin:
        return (
          <DigitalTwinLogic
            asset={ownedAsset!}
            onSubmit={(offer) => {
              onConfirm({
                ...offerForm,
                description: offer.description,
                amount: {
                  BRLA: {
                    amount: Number(offer.price),
                    checked: true,
                  },
                },
                address: offer.address,
                paymentData: offer.paymentData,
              })
            }}
          />
        )
      case ContractType.Fungible:
      case ContractType.RarumNFT:
      case ContractType.Loyalty:
        return (
          <CreateOfferView
            key={'confirm_step'}
            onGoBack={() =>
              history.goBackWithFallback(
                TO_COLLECTION_ITEM_DETAIL(id, ownedAsset!.tokenId)
              )
            }
            baseAsset={ownedAssetDetails}
            loading={!ownedAssetDetails}
            form={offerForm}
            currencies={currencies}
            canContinue={canContinue}
            tax={tax}
            howManyItemsICanSell={availableForSale || 0}
            compact={compact}
            onUpdateCurrency={(curr, active, amount) =>
              setDetails((prev) => ({
                ...prev,
                amount: {
                  [curr]: {
                    checked: active,
                    amount: amount,
                  },
                },
              }))
            }
            onUpdateDeadline={(dl) =>
              setDetails((prev) => ({ ...prev, selectedDeadline: dl }))
            }
            onUpdateQuantity={(dl) =>
              setDetails((prev) => ({ ...prev, selectedQuantity: dl }))
            }
            onSell={onConfirmSellOrUpdate}
            mode={
              !offerId
                ? CreateOfferFormMode.CREATION
                : CreateOfferFormMode.EDITION
            }
          />
        )
      default:
        return null
    }
  })()

  const KYCWrapper = gallery?.marketplace?.requirements
    ? OverlayKYCWrapperLogic
    : FragmentWrapper

  return (
    <KYCWrapper
      prereqTo='marketplace-sell'
      requirement={gallery?.marketplace}
      onClose={history.goBack}>
      {content}
    </KYCWrapper>
  )
}

export default function CreateOfferLogicWrapper({
  compact = false,
}: {
  compact?: boolean
}) {
  const scrollRoot = useScrollToTop()
  const { assetId: id } = useParams<{
    assetId: AssetType['id']
    offerId?: string
  }>()
  const { ownedItems } = useUpToDateMyCollectionItems()
  const specificTokenId = useQuery<{ tokenId?: string }>().get('tokenId')
  const ownedAsset = useMemo(() => {
    return ownedItems?.find(
      (a) =>
        a.id === id &&
        (specificTokenId ? a.tokenId === Number(specificTokenId) : true)
    )
  }, [ownedItems, id])
  const history = useCustomHistory()
  const [offerDetails, setOfferDetails] = useState<
    (OfferForm | DeepPartial<OfferForm>) & { offerId?: string }
  >()
  const { profile } = useUser()

  const marketplaceInterface = useMarketplaceInterface()

  const content = useMemo(() => {
    if (!marketplaceInterface || !profile?.isLoaded) return null

    const requiresMigration =
      marketplaceInterface instanceof OnchainMarketplace
        ? profile?.migrated
        : marketplaceInterface instanceof ZeroXMarketplace
        ? false
        : (() => {
            throw new Error(
              `Please review if the current marketplace interface requires migration`
            )
          })()

    if (requiresMigration) {
      return (
        <Redirect key='redirect' to={ROUTES.authenticated.transferTokens} />
      )
    } else if (!offerDetails) {
      return (
        <CreateOfferLogic
          compact={compact}
          key={'details'}
          onEdit={(details, offerId) => {
            setOfferDetails({
              ...details,
              offerId,
              asset: ownedAsset,
            })
          }}
          asset={ownedAsset}
          onConfirm={(details) => {
            scrollRoot()
            setOfferDetails({
              ...details,
              asset: ownedAsset,
            })
          }}
        />
      )
    } else {
      return (
        <WalletRequiredWrapper
          key='confirmation'
          allowCustody={!requiresMigration}>
          {!offerDetails.offerId ? (
            <Confirm
              asset={ownedAsset!}
              offer={offerDetails as OfferForm}
              onGoBack={() =>
                history.goBackWithFallback(
                  TO_COLLECTION_ITEM_DETAIL(id, ownedAsset!.tokenId)
                )
              }
            />
          ) : (
            <SettleOperationLogic
              type={SettleOperationType.MARKETPLACE_ITEM_EDITION}
              params={[offerDetails.offerId, offerDetails as OfferForm]}
              onBack={() =>
                history.goBackWithFallback(
                  TO_COLLECTION_ITEM_DETAIL(id, ownedAsset!.tokenId)
                )
              }
              onFinish={(offerId) =>
                history.replace(
                  TO_MARKETPLACE_OFFER_DETAILS(
                    id,
                    offerId!,
                    ownedAsset!.tokenId
                  )
                )
              }
            />
          )}
        </WalletRequiredWrapper>
      )
    }
  }, [
    offerDetails,
    compact,
    ownedAsset,
    history,
    id,
    profile,
    marketplaceInterface,
  ])
  if (!content) return content
  return <Transitions>{content}</Transitions>
}
